import { useEffect, useRef, useState } from 'react';

/**
 * A hook that listens for rapid keystrokes indicating a hardware barcode scanner.
 * Hardware scanners act like keyboards but type much faster than a human.
 * 
 * @param onScan Callback fired when a valid barcode is scanned
 * @param minLength Minimum length of the barcode to be considered valid
 * @param maxTimeBetweenKeystrokes Maximum allowed time between keystrokes (in ms) to be considered a scanner
 */
export const useBarcodeScanner = (
  onScan: (barcode: string) => void,
  minLength: number = 3,
  maxTimeBetweenKeystrokes: number = 150
) => {
  const bufferRef = useRef<string>('');
  const lastKeyTimeRef = useRef<number>(0);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if the focus is on an input field (optional: unless it's a specific input)
      // We'll allow it if they are focused on any element, but usually you don't want
      // the scanner to also type into random focused inputs.
      // However, if they are focused on a "Barcode" input, maybe we DO want it.
      // For a POS, usually, they can scan anywhere.
      // We will capture it globally but prevent default if it's a rapid scan.
      
      const now = Date.now();
      
      // If time since last keystroke is too long, reset the buffer (it's human typing)
      if (now - lastKeyTimeRef.current > maxTimeBetweenKeystrokes) {
        bufferRef.current = '';
      }

      // Record the time of this keystroke
      lastKeyTimeRef.current = now;

      // Handle the 'Enter' key which scanners append at the end
      if (e.key === 'Enter') {
        if (bufferRef.current.length >= minLength) {
          // Valid barcode scanned
          const scannedCode = bufferRef.current;
          onScan(scannedCode);
          
          // Clear buffer
          bufferRef.current = '';
          
          // Prevent default to stop form submissions if focused on an input
          e.preventDefault();
        } else {
          // Too short, just clear buffer
          bufferRef.current = '';
        }
        return;
      }

      // Only add single characters to the buffer (ignore Shift, Ctrl, Alt, etc.)
      if (e.key.length === 1) {
        bufferRef.current += e.key;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onScan, minLength, maxTimeBetweenKeystrokes]);
};
